import React, { useEffect } from 'react';
import { animateScroll as scroll } from 'react-scroll/modules';

import { InfoMessage, SuccessMessage } from '@eika/attention-message';
import { Icon, IconTypes } from '@eika/icon';

import Signing from '../../../components/Signing/Signing';
import StepButtons from '../../../components/Steps/StepButtons';
import { CompletionReducer } from '../../../reducers/completion';
import { ContextReducer } from '../../../reducers/context';
import { FeatureToggleReducer } from '../../../reducers/feature';
import SigningStatus from '../../../util/mappings/signingStatus';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface Props {
    completion: CompletionReducer;
    context: ContextReducer;
    feature: FeatureToggleReducer;
    refreshApplication: () => void;
}

const getCompletionReturnUrl = (context: ContextReducer, applicationId?: string): string => {
    const completionPath = context.config.data.completionPathSecuredLoan || window.location.pathname;
    const returnUrl = new URL(completionPath, window.location.origin);

    if (applicationId) {
        returnUrl.searchParams.set('id', applicationId);
    }

    return returnUrl.toString();
};

const getSigningRedirectUrl = (esigningUrl?: string, exitUrl?: string, cancelUrl?: string): string | undefined => {
    if (!esigningUrl) {
        return undefined;
    }

    const signingUrl = new URL(esigningUrl);

    if (exitUrl) {
        signingUrl.searchParams.set('exit', exitUrl);
    }

    if (cancelUrl) {
        signingUrl.searchParams.set('cancel', cancelUrl);
    }

    return signingUrl.toString();
};

const LoanCompletionSigning = ({ completion, context, feature, refreshApplication }: Props): JSX.Element => {
    useEffect(() => {
        trackEvent(TrackingStep.CompletionSigning);
        scroll.scrollToTop();
    }, []);

    const { text } = context.config.data;
    const { signingOrderId, esigningUrl, applicationId, esigningStatus } = completion.ongoing;
    const hasValidSigningOrderId = !!signingOrderId && /^\d+$/.test(signingOrderId);
    const shouldPollSigningProgress =
        feature.salgspantSigningRedirect &&
        !!applicationId &&
        [SigningStatus.notSigned, SigningStatus.ongoingSigning, SigningStatus.partiallySigned].includes(
            esigningStatus || '',
        );

    useEffect(() => {
        let intervalId: number | undefined;

        if (
            shouldPollSigningProgress ||
            (feature.salgspantSigningRedirect && !hasValidSigningOrderId && applicationId)
        ) {
            intervalId = window.setInterval(() => {
                refreshApplication();
            }, 2000);

            refreshApplication();
        }

        return () => {
            if (intervalId) {
                window.clearInterval(intervalId);
            }
        };
    }, [
        applicationId,
        feature.salgspantSigningRedirect,
        hasValidSigningOrderId,
        refreshApplication,
        shouldPollSigningProgress,
    ]);

    const getSigningText = (): string => {
        if (esigningStatus === SigningStatus.ongoingSigning) {
            return text.completionSigningMortgageDocText || text.completionSigningText;
        }

        if (esigningStatus === SigningStatus.notSigned) {
            return text.completionSigningDebtDocText || text.completionSigningText;
        }

        return text.completionSigningText;
    };

    return (
        <div className="signing">
            <h1>{text.completionSigningTitle}</h1>
            {esigningStatus === SigningStatus.ongoingSigning && (
                <SuccessMessage
                    text={text.completionSigningDebtDocSignedText || 'Gjeldsbrevet er signert.'}
                    icon={IconTypes.CHECKMARK_LISTE_24}
                />
            )}
            <p>{getSigningText()}</p>
            {feature.salgspantSigningRedirect ? (
                <StepButtons
                    showNext
                    nextChangeEvent={() => {
                        const returnUrl = getCompletionReturnUrl(context, applicationId);
                        const cancelUrl = `${window.location.origin}${context.config.data.landingPagePath}`;
                        const redirectUrl = getSigningRedirectUrl(esigningUrl, returnUrl, cancelUrl);

                        if (!redirectUrl) {
                            refreshApplication();
                            return;
                        }

                        window.location.href = redirectUrl;
                    }}
                    nextButtonName={
                        <>
                            {text.completionSigningButtonText} <Icon type={IconTypes.EKSTERN_24} />
                        </>
                    }
                />
            ) : (
                <Signing context={context} url={esigningUrl} />
            )}

            <InfoMessage
                className="signing__info-box"
                text={text.completionSigningInfoBox}
                icon={IconTypes.LYSPAERE_24}
            />
        </div>
    );
};

export default LoanCompletionSigning;
